import { Component } from "@theme/component";

/**
 * A custom element that handles product finder functionality with metaobject integration
 *
 * @typedef {Object} Refs
 * @property {HTMLFormElement} finderForm - The main finder form
 * @property {HTMLSelectElement} brandSelect - The brand select element
 * @property {HTMLSelectElement} yearSelect - The year select element
 * @property {HTMLSelectElement} modelSelect - The model select element
 * @property {HTMLInputElement} hiddenQuery - The hidden query input
 * @property {HTMLButtonElement} searchButton - The search button
 *
 * @extends {Component<Refs>}
 */
class ProductFinderComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#initializeProductData();
    this.#initializeState();
  }

  /**
   * Initialize product data from the global window object
   */
  #initializeProductData() {
    this.products = Array.isArray(window.productFinderProducts)
      ? window.productFinderProducts
      : [];

    // Build unique brand list from product make values
    const brands = new Set();
    this.products.forEach((product) => {
      if (product && typeof product.make === "string" && product.make.trim() !== "") {
        brands.add(product.make.trim());
      }
    });
    this.brands = Array.from(brands).sort((a, b) => a.localeCompare(b));

    this.#populateBrandOptions();
  }

  /**
   * Initialize the component state
   */
  #initializeState() {
    this.#updateSearchButtonState();
    this.#updateSelectStates();
  }

  /**
   * Handles option updates when selects change
   * @param {Event} event - The change event
   */
  updateOptions = (event) => {
    const changedSelect = event.target;

    const { brandSelect, yearSelect } = this.refs;

    // If brand changed, update year and model options
    if (changedSelect === this.refs.brandSelect) {
      this.#updateYearOptions();
      this.#updateModelOptions();
    }

    // If year changed, update model options based on year filter
    if (changedSelect === yearSelect) {
      this.#updateModelOptions();
    }

    // Enable/disable subsequent selects based on selections
    this.#updateSelectStates();

    // Update search button state
    this.#updateSearchButtonState();

    // Build search query for form submission
    this.#buildSearchQuery();
  };

  /**
   * Normalize raw year metafield value into an array of year strings
   * Handles cases like ["2014, 2016, 2017"] or "2014,2016"
   * @param {unknown} value
   * @returns {string[]}
   */
  #extractYears(value) {
    const rawItems = Array.isArray(value) ? value : value != null ? [value] : [];

    const years = [];

    rawItems.forEach((item) => {
      if (item == null) return;
      const str = String(item);
      // Split on comma and trim each part
      str.split(",").forEach((part) => {
        const trimmed = part.trim();
        if (trimmed !== "") {
          years.push(trimmed);
        }
      });
    });

    return years;
  }

  /**
   * Populate brand options from product makes
   */
  #populateBrandOptions() {
    const { brandSelect } = this.refs;
    if (!brandSelect) return;

    const placeholderText = brandSelect.querySelector("option")?.textContent || "Select Brand";
    brandSelect.innerHTML = `<option value="">${placeholderText}</option>`;

    this.brands.forEach((brand) => {
      const option = document.createElement("option");
      option.value = brand;
      option.textContent = brand;
      brandSelect.appendChild(option);
    });
  }

  /**
   * Update year options based on selected brand
   */
  #updateYearOptions() {
    const { brandSelect, yearSelect } = this.refs;
    if (!brandSelect || !yearSelect) return;

    const selectedBrand = brandSelect.value;
    const placeholderText = yearSelect.querySelector("option")?.textContent || "Select Year";
    yearSelect.innerHTML = `<option value="">${placeholderText}</option>`;

    if (!selectedBrand) return;

    const years = new Set();

    this.products.forEach((product) => {
      if (!product || product.make !== selectedBrand) return;

      this.#extractYears(product.year).forEach((y) => years.add(y));
    });

    Array.from(years)
      .sort((a, b) => a.localeCompare(b))
      .forEach((year) => {
        const option = document.createElement("option");
        option.value = year;
        option.textContent = year;
        yearSelect.appendChild(option);
      });
  }

  /**
   * Updates model options based on selected brand and year
   */
  #updateModelOptions() {
    const { brandSelect, yearSelect, modelSelect } = this.refs;

    if (!brandSelect || !modelSelect) return;

    const selectedBrand = brandSelect.value;
    const selectedYear = yearSelect?.value;

    const placeholderText = modelSelect.querySelector("option")?.textContent || "Select Model";
    modelSelect.innerHTML = `<option value="">${placeholderText}</option>`;

    if (!selectedBrand) return;

    const models = new Set();

    this.products.forEach((product) => {
      if (!product || product.make !== selectedBrand) return;

      if (selectedYear) {
        const yearsForProduct = this.#extractYears(product.year);
        if (!yearsForProduct.includes(selectedYear)) return;
      }

      if (typeof product.model === "string") {
        const modelName = product.model.trim();
        if (modelName !== "") {
          models.add(modelName);
        }
      }
    });

    Array.from(models)
      .sort((a, b) => a.localeCompare(b))
      .forEach((model) => {
        const option = document.createElement("option");
        option.value = model;
        option.textContent = model;
        modelSelect.appendChild(option);
      });
  }

  /**
   * Updates the enabled/disabled state of select elements
   */
  #updateSelectStates() {
    const { brandSelect, yearSelect, modelSelect } = this.refs;

    if (!brandSelect || !yearSelect || !modelSelect) return;

    // Year becomes enabled when brand is selected
    yearSelect.disabled = !brandSelect.value;

    // Model becomes enabled when brand is selected (and has models available)
    const hasModels = modelSelect.options.length > 1; // More than just placeholder
    modelSelect.disabled = !brandSelect.value || !hasModels;

    // Reset subsequent selects when a parent changes
    if (!brandSelect.value) {
      yearSelect.selectedIndex = 0;
      modelSelect.selectedIndex = 0;
    }

    if (!yearSelect.value) {
      // Don't reset model when year changes, as models depend on brand not year
    }
  }

  /**
   * Updates the search button enabled/disabled state
   */
  #updateSearchButtonState() {
    const { brandSelect, searchButton } = this.refs;

    if (!searchButton) return;

    // Require at least brand to be selected
    const hasMinimumSelection = brandSelect?.value;
    searchButton.disabled = !hasMinimumSelection;
  }

  /**
   * Builds the search query based on selected options
   */
  #buildSearchQuery() {
    const { brandSelect, yearSelect, modelSelect, hiddenQuery } = this.refs;

    if (!hiddenQuery) return;

    const queryParts = [];

    if (brandSelect?.value) {
      queryParts.push(brandSelect.value);
    }

    if (yearSelect?.value) {
      queryParts.push(yearSelect.value);
    }

    if (modelSelect?.value) {
      queryParts.push(modelSelect.value);
    }

    // Join the parts to create a search query
    hiddenQuery.value = queryParts.join(" ");
  }
}

if (!customElements.get("product-finder-component")) {
  customElements.define("product-finder-component", ProductFinderComponent);
}